/**
 * Archive file renderer (zip, tar, rar) with download prompt
 */

import { Stack, Text, Badge } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import { Button, Card } from '@/components/ui';
import { getFileIcon, getFileMimeColor, getFileTypeName } from '../../../utils';
import { formatFileSize } from '../../../../utils/formatting';
import type { ViewerFile } from '../../../types';

interface ArchiveRendererProps {
  file: ViewerFile;
  onDownload?: () => void;
}

export function ArchiveRenderer({ file, onDownload }: ArchiveRendererProps) {
  const FileIcon = getFileIcon(file.mimeType);
  const iconColor = getFileMimeColor(file.mimeType);
  const typeName = getFileTypeName(file.mimeType);

  return (
    <Stack align="center" justify="center" h="100%" gap="lg">
      <Card variant="elevated" p="xl" style={{ minWidth: 320, maxWidth: 420 }}>
        <Stack align="center" gap="md">
          <FileIcon size={48} color={iconColor} />
          <Text size="lg" fw={500} c="white" ta="center" lineClamp={2}>
            {file.fileName}
          </Text>
          <Badge variant="light" color="gray" size="lg">
            {typeName}
          </Badge>
          {file.fileSize > 0 && (
            <Text size="sm" c="dimmed">
              {formatFileSize(file.fileSize)}
            </Text>
          )}
          {onDownload && (
            <Button
              variant="gradient"
              gradientVariant="primary"
              leftSection={<IconDownload size={16} />}
              onClick={onDownload}
            >
              Download
            </Button>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
